/**
 * Created by Ning Jiang on 2021-06-04.
 */

import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/text/BitmapTextStyleOptions";
import {TombstoneRip} from "../TombstoneRip";
import {Layers, TombstoneRipLayers} from "./TombstoneRipLayers";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {TombstoneRipGameMode} from "../gameflow/TombstoneRipGameMode";
import {TombstoneRipGameAssets} from "../TombstoneRipGameAssets";
import {gsap, TimelineLite} from "gsap";
import {NolimitConfig} from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";
import {TombstoneRipSound} from "../sound/TombstoneRipSound";
import {SpineHelper} from "../../core/utils/SpineHelper";

export class Multiplier extends PIXI.Container {

    private readonly _numberStyle:BitmapTextStyleOptions = {
        fontName : TombstoneRipGameAssets.BITMAP_FONT_MULTIPLIER,
        fontSize : 50,
        tint : 0xffffff
    };

    private readonly _numberGlowStyle:BitmapTextStyleOptions = {
        fontName : TombstoneRipGameAssets.BITMAP_FONT_SUPERFS_GLOW_MUL,
        fontSize : 50,
        tint : 0xffffff
    };

    private _emptyHolder:PIXI.Sprite;
    private _numberText:PIXI.BitmapText;
    private _numberGlowText:PIXI.BitmapText;
    private _effectContainer:PIXI.Container;
    private _currentMultiplier:number = 0;
    private _isSuper:boolean;

    private _lastSoundIndex:number;
    private _lastSoundTime:number;

    public get currentMultiplier():number {
        return this._currentMultiplier;
    }

    constructor() {
        super();

        this._emptyHolder = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("multiplierHolder")[0]);
        this._emptyHolder.anchor.set(0.5);
        this._emptyHolder.position.set(4, 2);
        this.addChild(this._emptyHolder);

        this._effectContainer = new PIXI.Container();
        this.addChild(this._effectContainer);

        this._numberGlowText = new PIXI.BitmapText("", this._numberGlowStyle);
        (<PIXI.Point>this._numberGlowText.anchor).set(0.5);
        this._numberGlowText.scale.set(0.44);
        this._numberGlowText.y = -10;
        this._numberGlowText.letterSpacing = -14;
        this._numberGlowText.visible = false;
        this.addChild(this._numberGlowText);

        this._numberText = new PIXI.BitmapText("", this._numberStyle);
        (<PIXI.Point>this._numberText.anchor).set(0.5);
        this._numberText.letterSpacing = -8;
        this._numberText.visible = false;
        this.addChild(this._numberText);

        this.position.set(662, 105);
        TombstoneRipLayers.getLayer(Layers.FOREGROUND_TOP)?.addChild(this);
    }

    public updateMultiplier(value:number, animate:boolean):void {
        if(value === this._currentMultiplier) {
            return;
        }

        this._currentMultiplier = value;
        const tl = gsap.timeline();
        // if(animate && value > 0) {
        //     tl.add(this.playStone());
        // }
        tl.add(() => {
            this._numberText.text = `X${value.toString()}`;
            this._numberGlowText.text = `x${value.toString()}`;
            if(value > 0) {
                this._numberText.visible = !this._isSuper;
                this._numberGlowText.visible = this._isSuper;
                this._emptyHolder.visible = false;
            } else {
                this._numberText.visible = false;
                this._numberGlowText.visible = false;
                this._emptyHolder.visible = true;
            }
        });
        if(animate) {
            if(this._numberText.visible) {
                tl.add([
                    () => {this._numberText.scale.set(1.2)},
                    gsap.to(this._numberText.scale, {x : 1, y : 1, duration : 0.3})
                ]);
            }
            if(this._numberGlowText.visible) {
                tl.add([
                    () => {this._numberGlowText.scale.set(1.2 * 0.44)},
                    gsap.to(this._numberGlowText.scale, {x : 0.44, y : 0.44, duration : 0.3})
                ]);
            }
        }
    }

    public addMultiplier(value:number, log:string):void {
        // Logger.logDev(`MULTIPLIER: Adding multiplier from ${log}, old value ${this._currentMultiplier}, new value ${this._currentMultiplier + value}`);
        const endValue = this._currentMultiplier + value;
        this.playAddEffects(endValue);
        this.updateMultiplier(endValue, true);
    }

    private playAddEffects(value:number):void {
        if(value < 5) {
            return;
        }

        const now = Date.now();
        const soundIndex = this.getSoundIndex(value);
        if((now - this._lastSoundTime < 50) && soundIndex === this._lastSoundIndex) {
            return;
        }

        this._lastSoundTime = now;
        this._lastSoundIndex = soundIndex;
        TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.MULTIPLIER[soundIndex]});
        const effect = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("multiplier"));
        effect.autoUpdate = true;
        this._effectContainer.addChild(effect);
        const tl = new TimelineLite();
        tl.add(() => {
            effect.state.setAnimation(0, "glow", false);
        });
        tl.add(() => {
            SpineHelper.destroySpine(effect)
        }, effect.spineData.findAnimation("glow").duration);
    }

    private getSoundIndex(value:number):number {
        if(value < 8) {
            return 0;
        }
        if(value < 11) {
            return 1;
        }
        if(value < 14) {
            return 2;
        }
        if(value < 17) {
            return 3;
        }
        return 4;
    }

    public reset():void {
        if(this.isSticky(TombstoneRip.model.currentParsedData.nextMode)) {
            return;
        }

        this.updateMultiplier(0, false);
    }

    private isSticky(nextMode:string):boolean {
        return nextMode === GameMode.FREESPIN || nextMode === TombstoneRipGameMode.FREESPIN_SUPER;
    }

    public verifyTotalMultiplier():void {
        if(!NolimitConfig.isDevMode) {
            return;
        }

        const winMultiplier = TombstoneRip.model.parsedGameData.currentMultiplier;
        if(winMultiplier === this._currentMultiplier) {
            return;
        }
        if(TombstoneRip.model.parsedGameData.mode === GameMode.NORMAL && winMultiplier === 1 && this._currentMultiplier === 0) {
            return;
        }

        debugger;
        throw new Error("Displaying Multiplier doesn't match server data.");
    }

    public initFS():void {
        this.updateMultiplier(TombstoneRip.model.currentParsedData.currentMultiplier, false);
    }

    public resetAfterFS():void {
        if(TombstoneRip.model.parsedGameData.betWins.length === 0) {
            this.reset();
        } else {
            this._numberText.visible = true;
            this._numberGlowText.visible = false;
        }
    }

    public setColor(color:number, isSuper:boolean):void {
        this._isSuper = isSuper;
        this._numberText.tint = color;
    }
}